import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type PresignedZipEntry = { url: string; relative_path: string };

export type BrowserZipJob =
  | {
      kind: "from_source";
      label: string;
      source: string;
    }
  | {
      kind: "from_entries";
      label: string;
      file_entries: PresignedZipEntry[];
      filename: string;
      topFolderResumeSource?: string;
    };

export type BrowserZipState = {
  jobs: Record<string, BrowserZipJob>;
};

const initialState: BrowserZipState = {
  jobs: {},
};

const browserZipSlice = createSlice({
  name: "browserZip",
  initialState,
  reducers: {
    startBrowserZipFromSource: (
      state,
      action: PayloadAction<{
        itemKey: string;
        label: string;
        source: string;
      }>,
    ) => {
      state.jobs[action.payload.itemKey] = {
        kind: "from_source",
        label: action.payload.label,
        source: action.payload.source,
      };
    },
    startBrowserZipFromEntries: (
      state,
      action: PayloadAction<{
        itemKey: string;
        label: string;
        file_entries: PresignedZipEntry[];
        filename: string;
        topFolderResumeSource?: string;
      }>,
    ) => {
      const { itemKey, label, file_entries, filename, topFolderResumeSource } =
        action.payload;
      state.jobs[itemKey] = {
        kind: "from_entries",
        label,
        file_entries,
        filename,
        ...(topFolderResumeSource
          ? { topFolderResumeSource }
          : {}),
      };
    },
    removeBrowserZipJob: (
      state,
      action: PayloadAction<{ itemKey: string }>,
    ) => {
      delete state.jobs[action.payload.itemKey];
    },
    hydrateBrowserZip: (
      state,
      action: PayloadAction<BrowserZipState["jobs"]>,
    ) => {
      state.jobs = action.payload ?? {};
    },
    resetBrowserZip: () => initialState,
  },
});

export const {
  startBrowserZipFromSource,
  startBrowserZipFromEntries,
  removeBrowserZipJob,
  hydrateBrowserZip,
  resetBrowserZip,
} = browserZipSlice.actions;

export default browserZipSlice.reducer;
